'use client';

/**
 * Nomafé's own, aggregated-only analytics (Worker `nomafe-stats`). No cookies, no browser storage, no
 * identifier of any kind: the browser only says "this happened" (page, product, cart, checkout) and the
 * Worker adds 1 to a daily counter. Independent from GA4 and from analytics consent.
 *
 * Fully inert unless NEXT_PUBLIC_STATS_URL is set (e.g. https://stats.nomafe.com/e). Events are queued
 * and sent in one navigator.sendBeacon when the page is hidden or after a short pause; there are no
 * retries and every failure is swallowed, so tracking can never affect the page. It makes no request
 * on the server (SSR) and none to the main Worker.
 *
 * Level N1 only. (The Worker also understands optional N1+ fields; they are deliberately not sent.)
 */

const endpoint = process.env.NEXT_PUBLIC_STATS_URL?.trim();

type StatsEvent =
  | { t: 'pv'; p: string }
  | { t: 'vp'; pid: string; vid?: string }
  | { t: 'atc'; pid: string; q: number; vid?: string }
  | { t: 'rfc'; pid: string; q: number }
  | { t: 'bc' }
  | { t: 'ca' | 'cr'; k: 'a' | 'm' };

const MAX_PER_BEACON = 12; // the Worker rejects larger beacons
const MAX_QUEUE = 48; // beyond this, events are dropped (never grows without bound)
const FLUSH_DELAY_MS = 2500;

const queue: StatsEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let hooked = false;

export const statsEnabled = Boolean(endpoint);

/** 'gid://shopify/Product/123' → '123' (the Worker only accepts numeric ids). */
const numericId = (id: string | null | undefined) => /(\d{6,20})$/.exec(id ?? '')?.[1] ?? null;

function send(batch: StatsEvent[]) {
  if (!endpoint) return;
  const body = JSON.stringify({ v: 1, e: batch });
  try {
    // text/plain keeps it a "simple" request: no CORS preflight
    if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
  } catch {
    /* fall through to a single fetch */
  }
  try {
    void fetch(endpoint, { method: 'POST', body, keepalive: true, credentials: 'omit', headers: { 'Content-Type': 'text/plain;charset=UTF-8' } }).catch(() => undefined);
  } catch {
    /* tracking must never break the page */
  }
}

function flush() {
  if (timer) clearTimeout(timer);
  timer = undefined;
  while (queue.length) send(queue.splice(0, MAX_PER_BEACON));
}

function push(event: StatsEvent) {
  if (!endpoint || typeof window === 'undefined') return;
  if (queue.length >= MAX_QUEUE) return;
  queue.push(event);
  if (!hooked) {
    hooked = true;
    document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flush());
    window.addEventListener('pagehide', flush);
  }
  if (!timer) timer = setTimeout(flush, FLUSH_DELAY_MS);
}

/** `pathname` only (no query string or hash). */
export const statsPageView = (pathname: string) => push({ t: 'pv', p: pathname.split(/[?#]/)[0] });

export function statsProductView(productId: string, variantId?: string | null) {
  const pid = numericId(productId);
  const vid = numericId(variantId);
  if (pid) push(vid ? { t: 'vp', pid, vid } : { t: 'vp', pid });
}

export function statsAddToCart(productId: string, quantity: number, variantId?: string | null) {
  const pid = numericId(productId);
  const vid = numericId(variantId);
  const q = Math.min(99, Math.trunc(quantity));
  if (pid && quantity >= 1) push(vid ? { t: 'atc', pid, q, vid } : { t: 'atc', pid, q });
}

export function statsRemoveFromCart(productId: string | null | undefined, quantity: number) {
  const pid = numericId(productId);
  if (pid && quantity >= 1) push({ t: 'rfc', pid, q: Math.min(99, Math.trunc(quantity)) });
}

export const statsBeginCheckout = () => push({ t: 'bc' });

/** Counts a banner decision per category: 'a' = analytics, 'm' = marketing (aggregate only). */
export const statsConsent = (decision: 'granted' | 'denied', kind: 'a' | 'm') => push({ t: decision === 'granted' ? 'ca' : 'cr', k: kind });
